import type Decimal from "decimal.js";
import { useCallback, useEffect, useRef, useState } from "react";
import type { CurrencyPrimitive } from "@/api/cashier";
import { getDecimalSeparator } from "@/utils/helpers";
import {
  type AmountValidationError,
  useAmountValidation,
} from "./useAmountValidation";
import { useFormatAmount } from "./useFormatAmount";

type UseAmountInputOptions = {
  currency: CurrencyPrimitive;
  locale: string;
  min?: Decimal;
  max?: Decimal;
  enforceMax?: boolean;
  maxDecimalPlaces?: number;
  onChange?: (amount: Decimal | undefined) => void;
  onError?: (
    error: AmountValidationError | "",
    input: string | undefined
  ) => void;
};

export type UseAmountInputResult = {
  value: string;
  error: string | undefined;
  /** Pass to <input onChange={(e) => handleChange(e.target.value)} /> */
  handleChange: (raw: string) => void;
  /** Programmatic set (e.g. MAX button). */
  setFromDecimal: (d: Decimal) => void;
};

export const useAmountInput = ({
  currency,
  locale,
  min,
  max,
  enforceMax,
  maxDecimalPlaces = 2,
  onChange,
  onError,
}: UseAmountInputOptions): UseAmountInputResult => {
  const [value, setValue] = useState<string>("");
  const [error, setError] = useState<string | undefined>();

  const runValidation = useAmountValidation({
    currency,
    locale,
    min,
    max,
    enforceMax,
    maxDecimalPlaces,
  });
  const { formatInput, formatSanitizedInput } = useFormatAmount();

  // Latches once the limit-change effect below auto-clears an over/under-limit
  // value (e.g. a sibling input consuming more of a shared balance pool). While
  // latched, the effect must not silently re-validate the stale value back to
  // "valid" just because the limits loosened again — that re-consumes the pool,
  // shrinks the sibling's limit, and the two inputs ping-pong forever (infinite
  // render loop). Any explicit user action (typing, MAX button) clears it.
  const autoInvalidatedRef = useRef(false);

  const applyResult = useCallback(
    (result: ReturnType<typeof runValidation>) => {
      // Preserves a partially typed decimal tail (trailing separator or zeros) so it survives
      // reformatting, e.g. "5." or "5.10". Mirrors the display formatting of a valid in-progress input.
      const withDecimalTail = (sanitized: string) => {
        const decimalSep = getDecimalSeparator(locale);
        const separatorIdx = sanitized.indexOf(".");
        const intPart =
          separatorIdx === -1 ? sanitized : sanitized.slice(0, separatorIdx);
        const decTail =
          separatorIdx === -1 ? "" : sanitized.slice(separatorIdx);

        // in case of , locale the , is replaced with . in sanitization, for display replace it back.
        const decReplaced =
          decimalSep !== "." ? decTail.replace(".", decimalSep) : decTail;
        return formatInput(intPart, currency, locale) + decReplaced;
      };

      switch (result.status) {
        case "empty":
          setValue("");
          setError("");
          onChange?.(undefined);
          onError?.("", undefined);
          return;

        case "in-progress": {
          setValue(withDecimalTail(result.sanitized));
          setError("");
          onChange?.(result.decimal);
          return;
        }

        case "invalid":
          // For min/max errors the input is a well-formed number that is merely out of range, so keep
          // any partially typed decimal separator/tail. Only genuine format errors get fully reformatted.
          setValue(
            result.error === "format"
              ? formatInput(result.sanitized, currency, locale)
              : withDecimalTail(result.sanitized)
          );
          setError(result.message);
          onChange?.(undefined);
          onError?.(result.error, result.sanitized);
          return;

        case "valid":
          setValue(
            formatSanitizedInput(
              result.decimal,
              result.sanitized,
              currency,
              locale
            )
          );
          setError("");
          onChange?.(result.decimal);
          onError?.("", undefined);
          return;
      }
    },
    [
      formatInput,
      formatSanitizedInput,
      getDecimalSeparator,
      currency,
      locale,
      onChange,
      onError,
    ]
  );

  const handleChange = useCallback(
    (raw: string) => {
      // Explicit user-driven input (typing, MAX button) always re-arms the
      // latch below — only an effect-driven auto-clear should leave it set.
      autoInvalidatedRef.current = false;
      applyResult(runValidation(raw));
    },
    [runValidation, applyResult]
  );

  const setFromDecimal = useCallback(
    (d: Decimal) => handleChange(d.toString()),
    [handleChange]
  );

  useEffect(() => {
    if (!value) return;

    const result = runValidation(value);
    const isOutOfRange =
      result.status === "invalid" &&
      (result.error === "min" || result.error === "max");

    // Already latched from a previous auto-clear and the (unchanged, stale)
    // value now validates again: the limits loosened on their own, not
    // because the user did anything. Reset to empty instead of silently
    // reinstating the stale amount — resurrecting it here would re-consume a
    // shared limit and cause an infinite render loop with whatever sibling
    // input freed that room up. Clearing the field (rather than leaving the
    // old error frozen on screen) still lets the user see the field is free
    // to use again.
    if (autoInvalidatedRef.current && !isOutOfRange) {
      autoInvalidatedRef.current = false;
      applyResult({ status: "empty" });
      return;
    }

    autoInvalidatedRef.current = isOutOfRange;
    applyResult(result);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    min?.toString(),
    max?.toString(),
    maxDecimalPlaces,
    currency.code,
    locale,
  ]);

  return { value, error, handleChange, setFromDecimal };
};
